/**
 * Resolve o documento vendors/{id} vinculado ao UID de autenticação.
 * Usado no login VENDOR e nos listeners de pedidos.
 */
import { doc, getDoc, getDocs, setDoc, collection, query, where, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { Role, Vendor } from '../types';

const LINK_FIELDS = ['authUserId', 'uid', 'userId', 'ownerUid', 'ownerId'] as const;

type VendorLinkFields = {
  authUserId?: string;
  uid?: string;
  userId?: string;
  ownerUid?: string;
  ownerId?: string;
};

export function vendorMatchesAuthUid(vendor: Vendor, authUid: string | null): boolean {
  if (!authUid) return false;
  const raw = vendor as Vendor & VendorLinkFields;
  return (
    vendor.id === authUid ||
    raw.authUserId === authUid ||
    raw.uid === authUid ||
    raw.userId === authUid ||
    raw.ownerUid === authUid ||
    raw.ownerId === authUid
  );
}

async function resolveVendorByAuthUidInternal(authUid: string): Promise<Vendor | null> {
  const direct = await getDoc(doc(db, 'vendors', authUid));
  if (direct.exists()) {
    return { id: direct.id, ...direct.data() } as Vendor;
  }

  for (const field of LINK_FIELDS) {
    const snap = await getDocs(
      query(collection(db, 'vendors'), where(field, '==', authUid), limit(1))
    );
    if (!snap.empty) {
      const d = snap.docs[0];
      return { id: d.id, ...d.data() } as Vendor;
    }
  }
  return null;
}

export async function resolveVendorByAuthUid(
  authUid: string,
  timeoutMs = 15000
): Promise<Vendor | null> {
  if (!authUid?.trim()) return null;

  try {
    const result = await Promise.race([
      resolveVendorByAuthUidInternal(authUid),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    return result;
  } catch (e) {
    console.warn('[resolveVendorByAuthUid] Falha na busca:', e);
    return null;
  }
}

/** ID do documento vendors/ usado em orders e produtos. */
export async function resolveVendorDocIdForAuth(authUid: string): Promise<string | null> {
  const vendor = await resolveVendorByAuthUid(authUid);
  return vendor?.id ?? null;
}

const ROLES_NEVER_INFER_VENDOR: Role[] = [
  'OWNER',
  'SUPER_ADMIN',
  'CITY_HALL',
  'DRIVER',
  'SUPPLIER',
];

/**
 * Corrige role CLIENT (ou ausente) quando existe vendors/ vinculado ao UID.
 * Aprovação TPU/Owner atualiza vendorStatus mas nem sempre grava users.role.
 */
export async function resolveEffectiveUserRole(
  authUid: string,
  storedRole?: Role | string | null
): Promise<Role> {
  const role = (storedRole as Role) || 'CLIENT';
  if (role === 'VENDOR' || ROLES_NEVER_INFER_VENDOR.includes(role)) {
    return role;
  }
  const vendor = await resolveVendorByAuthUid(authUid, 8000);
  if (vendor) {
    return 'VENDOR';
  }
  return role;
}

/** Sincroniza users/{uid}.role quando inferimos VENDOR a partir de vendors/. */
export async function syncUserRoleIfVendor(
  authUid: string,
  effectiveRole: Role,
  storedRole?: Role | string | null
): Promise<void> {
  if (effectiveRole !== 'VENDOR' || storedRole === 'VENDOR') return;
  try {
    await setDoc(
      doc(db, 'users', authUid),
      { role: 'VENDOR', updatedAt: Date.now() },
      { merge: true }
    );
  } catch (e) {
    console.warn('[resolveVendorByAuthUid] Falha ao sincronizar users.role:', e);
  }
}
